import { isPS } from '@/utils';
import type { fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import type z from 'zod';
import type { ProjectSearchFormType } from '../_models/projects-search';
import type {
  indicatorDataNatureTypeEnum,
  indicatorTermEnum,
  indicatorTypeEnum,
} from './enum';

/**
 * 効果発現経路検索が不要かどうかを判定する
 *
 * KSシート（独立行政法人等）かつ出資状況表の場合は検索不要
 *
 * @returns 検索が不要な場合はtrue
 */
export const unNeedActivityIndicatorSearch = ({
  sheetType,
  fundSheetType,
}: {
  sheetType: z.infer<typeof sheetTypeEnum>;
  fundSheetType: z.infer<typeof fundSheetTypeEnum> | null;
}): boolean => {
  return sheetType === 'KS' && fundSheetType === 'investment';
};

/**
 * 指標キーワード検索の「成果目標・成果指標のみ」チェックボックスの表示状態を判定する
 *
 * - PSシステムの場合: 非表示（unNeed）
 * - 指標タイプが「成果」の場合: 有効（enable）
 * - それ以外: 無効（disable）
 *
 * @returns チェックボックスの表示状態
 */
export const shouldShowIndicatorKeywordCheckbox = (
  type: z.infer<typeof indicatorTypeEnum> | 'reset' | null,
): { result: 'unNeed' | 'disable' | 'enable' } => {
  if (isPS()) {
    return { result: 'unNeed' };
  }
  if (type === 'outcome') {
    return { result: 'enable' };
  }
  return { result: 'disable' };
};

/**
 * 「定性的指標のみを設定している事業」チェックボックスの表示状態を判定する
 *
 * - PSシステムの場合: 非表示（unNeed）
 * - 指標タイプが「成果」の場合: 有効（enable）
 * - それ以外: 無効（disable）
 *
 */
export const shouldShowHasOnlyQualitativeIndicator = (
  type: z.infer<typeof indicatorTypeEnum> | null,
): { result: 'unNeed' | 'disable' | 'enable' } => {
  if (isPS()) {
    return { result: 'unNeed' };
  }
  if (type === 'outcome') {
    return { result: 'enable' };
  }
  return { result: 'disable' };
};

/**
 * 指標期間（短期/中期/長期）フィールドの表示可否を判定する
 *
 * 以下の条件をすべて満たす場合に表示:
 * - 指標タイプが「成果」
 * - 「定性的指標のみを設定している事業」がチェックされていない
 *
 */
export const shouldShowIndicatorTerm = (
  type: z.infer<typeof indicatorTypeEnum> | 'reset' | null,
  hasOnlyQualitativeIndicator: boolean,
): boolean => {
  if (type === 'outcome' && !hasOnlyQualitativeIndicator) {
    return true;
  }
  return false;
};

/**
 * データ性質（定性的/定量的）フィールドの表示状態を判定する
 *
 * - PSシステムの場合: 非表示（unNeed）
 * - 指標タイプが「成果」かつ「定性的指標のみ」が未チェックの場合: 有効（enable）
 * - それ以外: 無効（disable）
 *
 */
export const shouldShowIndicatorDataNatureType = (
  type: z.infer<typeof indicatorTypeEnum> | 'reset' | null,
  hasOnlyQualitativeIndicator: boolean,
): { result: 'unNeed' | 'disable' | 'enable' } => {
  if (isPS()) {
    return { result: 'unNeed' };
  }
  if (type === 'outcome' && !hasOnlyQualitativeIndicator) {
    return { result: 'enable' };
  }
  return { result: 'disable' };
};

// 関数の入力パラメータ型
type ActivityIndicatorSearchParams = Pick<
  ProjectSearchFormType,
  | 'sheetType'
  | 'fundSheetType'
  | 'indicatorKeyword'
  | 'indicatorKeywordSearchOnlyTitleAndDescription'
  | 'indicatorType'
  | 'indicatorTerm'
  | 'indicatorDataNatureType'
  | 'hasOnlyQualitativeIndicator'
>;

// 関数の返り値型
type ActivityIndicatorSearchResult = {
  indicatorKeyword: string | null;
  indicatorKeywordSearchOnlyTitleAndDescription: true | null;
  indicatorType: z.infer<typeof indicatorTypeEnum> | null;
  indicatorTerm: z.infer<typeof indicatorTermEnum> | null;
  indicatorDataNatureType: z.infer<typeof indicatorDataNatureTypeEnum> | null;
  hasOnlyQualitativeIndicator: true | null;
};

/**
 * 効果発現経路（指標）検索のリクエストパラメータを取得する
 *
 * 画面上の表示条件に基づいて、適切なリクエストパラメータを返す。
 * 非表示や無効化されているフィールドの値はnullを返す。
 *
 * @param params - 検索フォームの値とシート種別情報
 * @returns APIリクエスト用のパラメータオブジェクト
 */
export const getActivityIndicatorSearchParams = ({
  sheetType,
  fundSheetType,
  indicatorKeyword,
  indicatorKeywordSearchOnlyTitleAndDescription,
  indicatorType,
  indicatorTerm,
  indicatorDataNatureType,
  hasOnlyQualitativeIndicator,
}: ActivityIndicatorSearchParams): ActivityIndicatorSearchResult | null => {
  // 効果発現経路（指標）検索が不要な場合は全てnullを返す
  if (unNeedActivityIndicatorSearch({ sheetType, fundSheetType })) {
    return null;
  }

  // 各条件をチェックして適切な値を返す
  const keywordCheckboxState =
    shouldShowIndicatorKeywordCheckbox(indicatorType);
  const hasOnlyQualitativeIndicatorState =
    shouldShowHasOnlyQualitativeIndicator(indicatorType);
  const showIndicatorTerm = shouldShowIndicatorTerm(
    indicatorType,
    hasOnlyQualitativeIndicator,
  );
  const dataNatureTypeState = shouldShowIndicatorDataNatureType(
    indicatorType,
    hasOnlyQualitativeIndicator,
  );

  return {
    indicatorKeyword: indicatorKeyword ?? null,
    indicatorKeywordSearchOnlyTitleAndDescription:
      keywordCheckboxState.result === 'enable' &&
      indicatorKeywordSearchOnlyTitleAndDescription
        ? true
        : null,
    indicatorType: indicatorType ?? null,
    indicatorTerm: showIndicatorTerm ? (indicatorTerm ?? null) : null,
    indicatorDataNatureType:
      dataNatureTypeState.result === 'enable'
        ? (indicatorDataNatureType ?? null)
        : null,
    hasOnlyQualitativeIndicator:
      hasOnlyQualitativeIndicatorState.result === 'enable' &&
      hasOnlyQualitativeIndicator
        ? true
        : null,
  };
};
